import { useState } from "react";
import { COUNTRY_OTP_PRICING, type OtpChannel } from "@codway-dev/shared";
import type { CallbackEvent } from "@shopify/polaris-types";

const BENEFITS = [
  {
    title: "Verificación en tiempo real:",
    text: "Cuando un cliente ingresa su número de teléfono, se envía instantáneamente un OTP (contraseña de un solo uso) a través de SMS o WhatsApp.",
  },
  {
    title: "Confirmación segura:",
    text: "El cliente ingresa el OTP para confirmar su número, asegurándose de que sea válido antes de completar el pedido.",
  },
  {
    title: "Prevención de fraude:",
    text: "Al verificar los números de teléfono, se reducen significativamente los pedidos falsos y las complicaciones relacionadas con la entrega.",
  },
];

const COUNTRY_NAMES: Record<string, string> = {
  AR: "Argentina",
  CL: "Chile",
  CO: "Colombia",
  EC: "Ecuador",
  ES: "España",
  GT: "Guatemala",
  MX: "México",
  PA: "Panamá",
  PE: "Perú",
  PY: "Paraguay",
  US: "Estados Unidos",
  VE: "Venezuela",
};

const SUPPORTED_COUNTRIES = Object.keys(COUNTRY_NAMES).sort((a, b) =>
  COUNTRY_NAMES[a].localeCompare(COUNTRY_NAMES[b])
);

export function OtpChannelExplainerPanel({
  channel,
  pricing,
}: {
  channel: OtpChannel;
  pricing: {
    sms_per_message_usd: number;
    whatsapp_per_message_usd: number;
    country_code: string;
  } | null;
}) {
  const [selectedCountry, setSelectedCountry] = useState(
    pricing?.country_code ?? "CO"
  );

  const heading =
    channel === "sms"
      ? "¿Cómo funciona el OTP de SMS?"
      : "¿Cómo funciona el OTP de WhatsApp?";

  const selectedPricing =
    COUNTRY_OTP_PRICING[selectedCountry] ?? COUNTRY_OTP_PRICING.DEFAULT;

  return (
    <s-section heading={heading}>
      <s-stack direction="block" gap="base">
        <s-stack direction="block" gap="small-300">
          {BENEFITS.map(({ title, text }) => (
            <div
              key={title}
              style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}
            >
              <span
                style={{
                  color: "#008060",
                  fontWeight: 700,
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              >
                ✓
              </span>
              <s-text>
                <strong>{title}</strong> {text}
              </s-text>
            </div>
          ))}
        </s-stack>

        <s-divider />

        <s-text>
          <strong>Detalles de precios</strong>
        </s-text>
        <s-text color="subdued">
          Tarifas competitivas disponibles por país.
        </s-text>

        <s-stack direction="inline" gap="large-400" alignItems="center">
          <s-box inlineSize="auto" maxInlineSize="220px">
            <s-select
              value={selectedCountry}
              onChange={(e: CallbackEvent<"s-select">) =>
                setSelectedCountry(e.currentTarget.value)
              }
            >
              {SUPPORTED_COUNTRIES.map((code) => (
                <s-option key={code} value={code}>
                  {COUNTRY_NAMES[code]}
                </s-option>
              ))}
            </s-select>
          </s-box>

          <s-stack direction="block" gap="small-100">
            <s-text>
              <strong>${selectedPricing.sms.toFixed(4)}</strong> / SMS
            </s-text>
            <s-text>
              <strong>${selectedPricing.whatsapp.toFixed(4)}</strong> / WhatsApp
              message
            </s-text>
          </s-stack>
        </s-stack>
      </s-stack>
    </s-section>
  );
}
